import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { EditTrackPayload, TrackData } from '@shared/interfacres/track';
import { Playlist } from '@shared/interfacres/playlist';
import { DatePipe, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TracksManagerStore } from '@data-access/manager/stores/tracks-manager.store';

import { debounceTime, firstValueFrom } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';

import { MatDialog } from '@angular/material/dialog';
import { ManageTrackDialog, TrackModel } from './manage-track-dialog/manage-track-dialog';
import { GenresManagerStore } from '@data-access/manager/stores/genres-manager.store';
import { SubGenresManagerStore } from '@data-access/manager/stores/subgenres-manager.store';
import { MatProgressBar } from '@angular/material/progress-bar';
import { PlaylistsManagerStore } from '@data-access/manager/stores/playlists-manager.store';
import { AddToPlaylistDialog } from './add-to-playlist-dialog/add-to-playlist-dialog';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCheckbox } from '@angular/material/checkbox';
import { IconInput } from '@shared/components/icon-input/icon-input';
import { SecondsToTimePipe } from '@shared/pipes/secondsToMinutes';
import { UploadBulkDialog } from './upload-bulk-dialog/upload-bulk-dialog';
import { PlayerSourceService } from '@shared/components/player-bar/player-source.service';
import { ConfirmationDialog } from '@shared/components/confirmation-dialog/confirmation-dialog';
import { CustomPaginator, PAGINATOR_LABEL } from '@shared/components/custom-paginator/custom-paginator';
import { dateWithoutTimezone } from '@shared/helpers/common.helper';


@Component({
  selector: 'app-tracks',
  templateUrl: './tracks.html',
  styleUrl: './tracks.scss',
  providers: [{provide: PAGINATOR_LABEL, useValue: 'Tracks per page: '}],
  imports: [MatFormFieldModule, MatInputModule, MatTableModule, MatSortModule, MatPaginatorModule, DatePipe, MatIcon,
    MatSlideToggle, ReactiveFormsModule, NgClass, MatProgressBar, CdkDropList, CdkDrag, MatButton, MatCheckbox,
    IconInput, SecondsToTimePipe, CustomPaginator],
})
export class Tracks implements OnInit, OnDestroy {
  displayedColumns: string[] = ['drag', 'id', 'title', 'artists', 'duration', 'download', 'releaseDate', 'enable', 'actions'];
  dataSource: TrackData[] = [];
  tracksTotal = 0;

  currentTrack: TrackData | null = null;

  searchValue = new FormControl('');

  selection = new SelectionModel<number>(
    true,
    []
  );

  isTracksAttributesLoaded = computed(() => {
    return this.trackAttributes.genresList().length > 0 &&
      this.trackAttributes.subgenresList().length > 0;
  })

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('table', { static: true }) table: MatTable<TrackData> | undefined;

  // @ViewChild('player') player!: ElementRef<HTMLAudioElement>;

  readonly store = inject(TracksManagerStore);
  readonly genreStore = inject(GenresManagerStore);
  readonly subgenreStore = inject(SubGenresManagerStore);
  readonly playlistsStore = inject(PlaylistsManagerStore);
  readonly dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  playerSource = inject(PlayerSourceService);

  dragDisabled = true;
  dragInProcess = false;

  private trackAttributes = {
    genresList: this.genreStore.fullList,
    subgenresList: this.subgenreStore.fullList
  };

  tracksInPlaylist = new Set();

  constructor(
  ) {
    effect(() => {
      this.dataSource = this.store.tracks();
      this.tracksTotal = this.store.tracksTotal();

    });

    effect(() => {

      if (this.playlistsStore.fullList().length === 0) {
        return;
      }

      for (const playlist of this.playlistsStore.fullList()) {
        if (!playlist.tracks) {
          continue;
        }
        for (const track of playlist.tracks) {
          this.tracksInPlaylist.add(track.id);
        }
      }

    });
  }

  ngOnInit(): void {
    this.genreStore.getAll();
    this.subgenreStore.getAll();
    this.playlistsStore.getAll();
    this.searchValue.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(400),
      )
      .subscribe(input => {
        this.store.getTrackByName(input as string);
    })

    this.store.setFilter('releaseDate', 'desc');
  }

  changePage(page: {pageIndex: number; pageSize: number}) {
    this.store.setPagination(page.pageIndex + 1, page.pageSize);
  }

  sortChange(event: {active: string; direction: string}) {
    this.store.setFilter(event.active, event.direction as 'asc' | 'desc');
  }

  async openManageDialog(track: TrackData) {
    if (!this.isTracksAttributesLoaded()) {
      return;
    }
    const dialogRef = this.dialog.open(ManageTrackDialog, {
      data: {
        track,
        // artistsList: this.trackAttributes.artistsList(),
        genresList: this.trackAttributes.genresList(),
        subgenresList: this.trackAttributes.subgenresList(),
      },
      disableClose: true,
      width: '90%',
      maxWidth: '1600px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {

      const trackData: Partial<EditTrackPayload> = {
        title: result.title,
        artists: result.artists,
        bpm: result.bpm,
        releaseDate: dateWithoutTimezone(result.releaseDate) || dateWithoutTimezone(new Date()),
        genreId: result.genre?.id,
        subgenreId: result.subgenre?.id,
      }
      this.store.updatePartial(track.id!, trackData);
    }

  }

  async addToPlaylist(track: TrackData) {

    const dialogRef = this.dialog.open(AddToPlaylistDialog, {
      data: {
        track,
        playlists: this.playlistsStore.fullList(),
      },
      disableClose: true,
      width: '600px',
      maxWidth: '600px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result && result.length > 0) {
      const playlistIds = result.map((item: Playlist) => item.id);

      await this.playlistsStore.setPlaylistsForTrack(track.id!, playlistIds);
      this.playlistsStore.getAll();
    }
  }

  async openUploadDialog() {
    if (!this.isTracksAttributesLoaded()) {
      return;
    }
    const dialogRef = this.dialog.open(UploadBulkDialog, {
      data: {
        genresList: this.trackAttributes.genresList(),
        subgenresList: this.trackAttributes.subgenresList(),
      },
      disableClose: true,
      width: '90%',
      maxWidth: '1600px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      await this.store.saveTracksData(
        result.sessionId,
        result.tracks.map((track: TrackModel) => {
          return {
            ...track,
            releaseDate: track.releaseDate ? dateWithoutTimezone(track.releaseDate) : dateWithoutTimezone(new Date()),
            currentArtist: undefined,
            success: true,
          }
        })
      )
    }
  }

  async bulkDelete() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'Delete selected',
        message: 'Are you sure you want to delete selected tracks?'
      },
      disableClose: true,
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result) {

      this.store.delete(this.selection.selected);
      this.selection.clear();
    }

  }

  changeStatus(trackId: number, checked: boolean) {
    this.store.updatePartial(trackId, {enabled: checked});
  }

  async drop(event: CdkDragDrop<string>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    if (this.dragInProcess) {
      return;
    }
    this.dragInProcess = true;
    const previousIndex = this.dataSource.findIndex((d) => d === event.item.data);
    moveItemInArray(this.dataSource, previousIndex, event.currentIndex);
    this.table!.renderRows();

    const item = this.dataSource[event.currentIndex];
    let previousItemPosition = event.currentIndex > 0 ?
      this.dataSource[event.currentIndex - 1].position : null;

    let nextItemPosition = this.dataSource[event.currentIndex + 1] ?
      this.dataSource[event.currentIndex + 1].position : null;

    if (!previousItemPosition) {
      if (this.store.pagination().page !== 1) {
        const pagination = this.store.pagination();
        const withPagination = await this.store.getTracksOnPage(pagination.page - 1, pagination.pageSize);

        if (withPagination?.data && withPagination?.data?.length > 0) {
          previousItemPosition = withPagination.data[withPagination?.data?.length - 1].position;
        }
      }
    }

    if (!nextItemPosition) {
      const pagination = this.store.pagination();
      const withPagination = await this.store.getTracksOnPage(pagination.page + 1, pagination.pageSize);

      if (withPagination?.data && withPagination?.data?.length > 0) {
        nextItemPosition = withPagination.data[0].position;
      }

    }

    await this.store.setPosition(item.id, previousItemPosition, nextItemPosition);
    this.dragInProcess = false;
  }

  async play(track: TrackData) {
    this.playerSource.init(true);

    if (this.playerSource.currentTrack() && this.playerSource.currentTrack()?.id === track.id) {
      // track pressed again
      if (this.playerSource.isPlaying()) {
        this.playerSource.pause();
      } else {
        this.playerSource.play();
      }

      return;
    }

    this.currentTrack = track;
    this.playerSource.setTracksList([track], 0, true);
  }

  ngOnDestroy() {

    this.store.clearCriteria();
    this.playerSource.clear();
  }
}
